// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Delegated administration and break-glass grants.
 *
 * `SEC-016` prohibits indefinite elevation or break-glass, and `OPS-045`
 * requires break-glass grants to be reviewable. A grant without an expiry is
 * therefore called out explicitly rather than shown as an empty cell, and
 * break-glass grants are visually and textually distinguished.
 *
 * This screen reports the access model. It is not the enforcement point:
 * `CST-048` keeps every decision on the server.
 */

import { capabilities, queryKeys, StateBadge, type AccessGrant } from '@eai/ui-shared';
import type { ReactNode } from 'react';

import { ResourceScreen, useControlPlaneQuery } from '../app-shell';

/** Delegated administration screen. */
export function AccessScreen(): ReactNode {
  const query = useControlPlaneQuery(
    (scope) => queryKeys.controlPlane.accessGrants(scope),
    (client, signal) => client.listAccessGrants({ signal }),
  );

  return (
    <ResourceScreen<AccessGrant>
      title="Access"
      description="Delegated administrative grants and their expiry. Authorisation is enforced by the service; this screen reports what has been granted."
      requiredCapability={capabilities.accessRead}
      area="Access"
      query={query}
      rowKey={(row) => row.grant_id}
      emptyTitle="No delegated grant exists"
      emptyDescription="Delegate administration through the access workflow to populate this list."
      columns={[
        { key: 'subject', header: 'Subject', render: (row) => row.subject_label },
        { key: 'role', header: 'Role', render: (row) => row.role.name },
        { key: 'scope', header: 'Scope', render: (row) => row.scope_label },
        {
          key: 'granted',
          header: 'Granted',
          render: (row) => `${row.granted_at} by ${row.granted_by}`,
        },
        {
          key: 'expiry',
          header: 'Expiry',
          render: (row) =>
            row.expires_at ?? (
              <StateBadge
                label="No expiry set"
                tone="caution"
                subject={`${row.subject_label} grant expiry`}
              />
            ),
        },
        {
          key: 'breakGlass',
          header: 'Break-glass',
          render: (row) => (
            <StateBadge
              label={row.break_glass ? 'Break-glass' : 'Standard'}
              tone={row.break_glass ? 'negative' : 'neutral'}
              subject={`${row.subject_label} grant type`}
            />
          ),
        },
      ]}
    />
  );
}
